import React, { useState, useContext } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import PasswordInput from '../components/PasswordInput';
import AuthContext from '../context/AuthContext';

const RegisterPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { applyAuthTokens } = useContext(AuthContext);
    // El plan elegido en la sección de precios de la landing llega como
    // /register?plan=growth (LandingPage.js) — se reenvía tal cual al backend, que ya
    // sabe resolverlo y cae de vuelta a "entrepreneur" si viene vacío o inválido.
    const [searchParams] = useSearchParams();
    const selectedPlan = searchParams.get('plan');
    const [errors, setErrors] = useState([]);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '' // Changed from business_name
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors([]);

        const { email, password, confirmPassword, name } = formData;

        if (password !== confirmPassword) {
            return setErrors([t('registerPage.passwordsDoNotMatch')]);
        }

        try {
            const response = await api.post('/auth/register', {
                email, password, confirmPassword, name,
                ...(selectedPlan && { plan: selectedPlan }),
            });
            // Tarea 027c — register ya devuelve token/refreshToken: el usuario queda con
            // sesión iniciada de inmediato, en modo "pendiente de verificar" (mismo destino
            // que un login normal). Ya no pasa por /login.
            await applyAuthTokens(response.data);
            navigate('/sales');
        } catch (error) {
            console.error('Failed to register', error);
            // El mensaje que manda el backend es técnico y en inglés (para logs/soporte, no
            // para el usuario) — nunca se muestra tal cual; siempre se traduce a algo accionable.
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors.map(key => t(key)));
            } else if (error.response?.status === 409) {
                setErrors([t('registerPage.emailAlreadyExists')]);
            } else if (!error.response) {
                setErrors([t('registerPage.networkError')]);
            } else {
                setErrors([t('registerPage.registrationFailed')]);
            }
        }
    };

    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
            <Row>
                <Col>
                    <Card style={{ width: '24rem' }}>
                        <Card.Body>
                            <Card.Title className="text-center mb-4">{t('registerPage.title')}</Card.Title>
                            {errors.length > 0 && (
                                <Alert variant="danger">
                                    {errors.map((errorMsg, index) => (
                                        <div key={index}>{errorMsg}</div>
                                    ))}
                                </Alert>
                            )}
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>{t('registerPage.nameLabel')}</Form.Label>
                                    <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} placeholder={t('registerPage.namePlaceholder')} required />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="email">
                                    <Form.Label>{t('registerPage.emailLabel')}</Form.Label>
                                    <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('registerPage.emailPlaceholder')} required />
                                </Form.Group>

                                <PasswordInput
                                    controlId="password"
                                    label={t('registerPage.passwordLabel')}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder={t('registerPage.passwordLabel')}
                                    required
                                />
                                {errors.length > 0 && (
                                    <Form.Text className="text-danger">
                                        <ul className="list-unstyled">
                                            {errors.map((errorMsg, index) => (
                                                <li key={index}>{errorMsg}</li>
                                            ))}
                                        </ul>
                                    </Form.Text>
                                )}
                                
                                <PasswordInput
                                    controlId="confirmPassword"
                                    label={t('registerPage.confirmPasswordLabel')}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder={t('registerPage.confirmPasswordLabel')}
                                    required
                                />

                                <Form.Group className="mb-3" controlId="formBasicCheckbox">
                                    <Form.Check
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        label={
                                            <span>
                                                {t('registerPage.iHaveReadAndAccept')}
                                                <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer"> {t('registerPage.termsOfService')}</Link>,
                                                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer"> {t('registerPage.privacyPolicy')}</Link> {t('registerPage.andThe')}
                                                <Link to="/dpa" target="_blank" rel="noopener noreferrer"> {t('registerPage.dpa')}</Link>.
                                            </span>
                                        }
                                        required
                                    />
                                </Form.Group>

                                <div className="d-grid">
                                    <Button variant="primary" type="submit" style={{ backgroundColor: '#FF6A00', borderColor: '#FF6A00' }} disabled={!agreedToTerms}>
                                        {t('registerPage.registerButton')}
                                    </Button>
                                </div>
                            </Form>
                            <div className="text-center mt-3">
                                <Link to="/login">{t('registerPage.loginPrompt')}</Link>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default RegisterPage;